import { flatten, get, keyBy, sortBy } from 'lodash';
import i18next from 'i18next';

import { getProductPriceExcludingVAT } from '@admin/products/products/detail/components/ProductInfo/utils/getProductPriceExcludingVAT';

import { translateUnit } from '@commons/utils/translateUnit';

const _getComponentCost = (entity, channelId) => {
  const componentCost = entity.costByChannelId[channelId];

  return componentCost == null ? null : componentCost * entity.packagingValue;
};

/**
 * Format API recipe list into the shape expected by XLS export utilities.
 *
 * @param {Object[]} recipes - Raw recipes from the API (with allergens, compositionSettings, etc.)
 * @param {boolean} hasMultipleBrands
 * @param {Object[]} channels - Client channels
 * @returns {Object[]} Sorted formatted recipe list
 */
export const formatListOfRecipes = (recipes, hasMultipleBrands = false, channels = []) => {
  const formattedRecipes = recipes.reduce((res, item) => {
    const associatedProduct = item.product;
    const allergensNames = (item.allergens || [])
      .map(({ translationKey }) => i18next.t(`ADMIN.INGREDIENTS.${translationKey}`))
      .sort();
    const recipeBrands = hasMultipleBrands ? item.recipe.brands : [];

    const priceByChannelId = channels.reduce((acc, channel) => {
      acc[channel.id] = getProductPriceExcludingVAT(associatedProduct, channel.id);

      return acc;
    }, {});

    const commonFields = {
      id: item.recipe.id,
      name: item.recipe.name,
      associatedProductName: get(associatedProduct, 'name', null),
      priceByChannelId,
      unit: translateUnit(item.recipe.unit).toLowerCase(),
      quantity: item.recipe.value,
      category: item.recipe.category || i18next.t('GENERAL.NONE_VALUE'),
      status: item.recipe.active,
      allergensNames,
      components: item.compositionSettings,
      brands: recipeBrands,
      hasMultipleChannels: item.recipe.hasMultipleChannels,
      hasStock: item.recipe.hasStock,
      hasLoss: item.recipe.hasLoss,
      hasTransfer: item.recipe.hasTransfer,
    };

    res.push(commonFields);

    return res;
  }, []);

  return sortBy(formattedRecipes, 'name');
};

/**
 * Expand formatted recipes into one row per recipe×component with per-channel cost data.
 *
 * @param {Object[]} formattedRecipes - Output of formatListOfRecipes
 * @param {Object[]} channels - Client channels
 * @param {string} defaultChannelId - ID of the client's default channel
 * @returns {Object[]} Sorted flattened recipe-component rows
 */
export const formatListOfRecipesWithComponents = (
  formattedRecipes,
  channels = [],
  defaultChannelId,
) => {
  const formattedRecipesWithCompositions = formattedRecipes.map((formattedRecipe) => {
    const formattedRecipeDetail = {
      recipeId: formattedRecipe.id,
      name: formattedRecipe.name,
      associatedProductName: formattedRecipe.associatedProductName,
      unit: formattedRecipe.unit,
      quantity: formattedRecipe.quantity,
      category: formattedRecipe.category || i18next.t('GENERAL.NONE_VALUE'),
      status: formattedRecipe.status,
      brands: formattedRecipe.brands,
    };

    const recipeTotalCostByChannelId = Object.fromEntries(
      channels.map((channel) => [channel.id, 0]),
    );

    let recipeTotalDefaultCost = 0;

    const formattedCompositions = formattedRecipe.components.map(
      ({ entity, allergens: componentAllergens }) => {
        const componentIsIngredient = entity.isIngredient;

        const componentAllergensNames = componentAllergens.map(({ name }) => name).sort();

        const formattedCategory = entity.category || i18next.t('GENERAL.NONE_VALUE');

        const componentEntityDetail = {
          componentId: entity.id,
          componentName: entity.name,
          componentType: i18next.t(componentIsIngredient ? 'GENERAL.INGREDIENT' : 'GENERAL.RECIPE'),
          componentCategory: formattedCategory,
          componentQuantity: entity.packagingValue,
          componentUnit: translateUnit(entity.unit).toLowerCase(),
          componentAllergensNames,
          isIngredient: entity.isIngredient,
          isPreparationIngredient: entity.isPreparationIngredient,
        };

        if (channels.length <= 1) {
          const rawCost = componentIsIngredient
            ? entity.cost
            : entity.costByChannelId[defaultChannelId];

          const componentCost = rawCost == null ? null : rawCost * entity.packagingValue;

          if (!entity.isPreparationIngredient) {
            recipeTotalDefaultCost += componentCost ?? 0;
          }

          return {
            ...formattedRecipeDetail,
            ...componentEntityDetail,
            componentCost,
          };
        }

        const componentIsUsedByChannelId = Object.fromEntries(
          channels.map((channel) => [
            channel.id,
            entity.channels.some(({ id }) => id === channel.id),
          ]),
        );

        const componentCostByChannelId = Object.fromEntries(
          channels.map((channel) => {
            if (componentIsIngredient) {
              return [channel.id, entity.cost == null ? null : entity.cost * entity.packagingValue];
            }

            const costForChannel = entity.hasMultipleChannels
              ? _getComponentCost(entity, channel.id)
              : _getComponentCost(entity, defaultChannelId);

            return [channel.id, costForChannel];
          }),
        );

        if (!entity.isPreparationIngredient) {
          channels.forEach((channel) => {
            if (componentIsUsedByChannelId[channel.id]) {
              recipeTotalCostByChannelId[channel.id] =
                (recipeTotalCostByChannelId[channel.id] || 0) +
                (componentCostByChannelId[channel.id] || 0);
            }
          });
        }

        return {
          ...formattedRecipeDetail,
          ...componentEntityDetail,
          componentIsUsedByChannelId,
          componentCostByChannelId,
        };
      },
    );

    const formattedCompositionsWithRecipeTotalCost = formattedCompositions.map((composition) => ({
      ...composition,
      ...(channels.length > 1
        ? { costByChannelId: { ...recipeTotalCostByChannelId } }
        : { defaultCost: recipeTotalDefaultCost }),
    }));

    return formattedCompositionsWithRecipeTotalCost;
  });

  const flattenedRecipesWithCompositions = flatten(formattedRecipesWithCompositions);

  return sortBy(flattenedRecipesWithCompositions, ['name', 'componentName']);
};

/**
 * Build the export rows from raw API recipes: one recipe-level row per recipe (with total cost and,
 * for multi-channel clients, per-channel margin) and the recipe×component rows. Shared by the recipe
 * exports.
 *
 * @param {Object[]} recipes - Raw recipes from the API (with composition)
 * @param {boolean} hasMultipleBrands
 * @param {Object[]} channels - Client channels
 * @param {string} defaultChannelId
 * @returns {{ recipeRows: Object[], componentRows: Object[] }}
 */
export const buildExportRecipeRows = (recipes, hasMultipleBrands, channels, defaultChannelId) => {
  const formattedRecipes = formatListOfRecipes(recipes, hasMultipleBrands, channels);

  const componentRows = formatListOfRecipesWithComponents(
    formattedRecipes,
    channels,
    defaultChannelId,
  );

  const componentsByRecipeId = keyBy(componentRows, 'recipeId');

  const isMultiChannel = channels.length > 1;

  const recipeRows = formattedRecipes.map((recipe) => {
    const concernedRecipe = componentsByRecipeId[recipe.id] || {};

    if (isMultiChannel) {
      const marginByChannelId = channels.reduce((acc, channel) => {
        const priceHT = recipe.priceByChannelId[channel.id];
        const cost = concernedRecipe.costByChannelId?.[channel.id];

        acc[channel.id] =
          priceHT != null && cost != null ? ((priceHT - cost) / priceHT) * 100 : null;

        return acc;
      }, {});

      return {
        ...recipe,
        costByChannelId: concernedRecipe.costByChannelId,
        marginByChannelId,
      };
    }

    return { ...recipe, defaultCost: concernedRecipe.defaultCost };
  });

  return { recipeRows, componentRows };
};
